import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CrmDrilldownPage,
  CrmDrilldownQuery,
  CrmReport,
  CrmReportQuery,
  FinanceDrilldownPage,
  FinanceDrilldownQuery,
  FinanceReport,
  FinanceReportQuery,
  InventoryDrilldownQuery,
  InventoryDrilldownRow,
  InventoryReport,
  InventoryReportQuery,
  OverviewReport,
  OverviewReportQuery,
  ReportingSettingsView,
  SalesDrilldownQuery,
  SalesDrilldownRow,
  SalesReport,
  SalesReportQuery,
  UpdateReportingSettingsRequest,
} from '@optiflow/types';
import { api } from '@/services/apiClient';

export const reportKeys = {
  all: ['reports'] as const,
  overview: (query: OverviewReportQuery) => ['reports', 'overview', query] as const,
  sales: (query: SalesReportQuery) => ['reports', 'sales', query] as const,
  salesRecords: (query: SalesDrilldownQuery | Record<string, never>) =>
    ['reports', 'sales', 'records', query] as const,
  inventory: (query: InventoryReportQuery) => ['reports', 'inventory', query] as const,
  inventoryRecords: (query: InventoryDrilldownQuery | Record<string, never>) =>
    ['reports', 'inventory', 'records', query] as const,
  finance: (query: FinanceReportQuery) => ['reports', 'finance', query] as const,
  financeRecords: (query: FinanceDrilldownQuery | Record<string, never>) =>
    ['reports', 'finance', 'records', query] as const,
  crm: (query: CrmReportQuery) => ['reports', 'crm', query] as const,
  crmRecords: (query: CrmDrilldownQuery | Record<string, never>) =>
    ['reports', 'crm', 'records', query] as const,
  settings: ['reports', 'settings'] as const,
};

/** Drops empty values so the server resolves its own defaults rather than reading "". */
function cleanQuery<T extends object>(query: T): T {
  return Object.fromEntries(
    Object.entries(query).filter(([, value]) => value !== undefined && value !== ''),
  ) as T;
}

/**
 * The live Overview. Every figure, and which panels exist at all, is decided by the server for
 * this user: a panel missing from the response is one the user may not see, never a zero.
 */
export function useOverviewQuery(query: OverviewReportQuery, { enabled = true } = {}) {
  return useQuery({
    queryKey: reportKeys.overview(cleanQuery(query)),
    queryFn: () => api.get<OverviewReport>('/reports/overview', { params: cleanQuery(query) }),
    placeholderData: (previous) => previous,
    enabled,
  });
}

/**
 * The Sales tab.
 *
 * The same five headline figures as the Overview — computed by the same server-side layer — plus
 * the breakdowns. Filters travel as ids only; names come back resolved in `filters`.
 */
export function useSalesReportQuery(query: SalesReportQuery, { enabled = true } = {}) {
  return useQuery({
    queryKey: reportKeys.sales(cleanQuery(query)),
    queryFn: () => api.get<SalesReport>('/reports/sales', { params: cleanQuery(query) }),
    placeholderData: (previous) => previous,
    enabled,
  });
}

/** The records behind one figure, paged by the server. Only opened when a reader asks. */
export function useSalesRecordsQuery(query: SalesDrilldownQuery | null) {
  return useQuery({
    queryKey: reportKeys.salesRecords(query ? cleanQuery(query) : {}),
    queryFn: () =>
      api.get<{
        rows: SalesDrilldownRow[];
        total: number;
        page: number;
        pageSize: number;
      }>('/reports/sales/records', { params: cleanQuery(query as SalesDrilldownQuery) }),
    enabled: query !== null,
  });
}

/**
 * The Inventory tab. Current stock is as of now whatever the period; the period narrows flows.
 * Value and the zero-sales list are simply absent when the server did not authorise them.
 */
export function useInventoryReportQuery(query: InventoryReportQuery, { enabled = true } = {}) {
  return useQuery({
    queryKey: reportKeys.inventory(cleanQuery(query)),
    queryFn: () => api.get<InventoryReport>('/reports/inventory', { params: cleanQuery(query) }),
    placeholderData: (previous) => previous,
    enabled,
  });
}

/**
 * The Finance tab. Receivables and payables are as of now whatever the period; each section is
 * present only when the server authorised it and every active filter can narrow it.
 */
export function useFinanceReportQuery(query: FinanceReportQuery, { enabled = true } = {}) {
  return useQuery({
    queryKey: reportKeys.finance(cleanQuery(query)),
    queryFn: () => api.get<FinanceReport>('/reports/finance', { params: cleanQuery(query) }),
    placeholderData: (previous) => previous,
    enabled,
  });
}

/** The records behind one Finance figure, paged and totalled by the server. */
export function useFinanceRecordsQuery(query: FinanceDrilldownQuery | null) {
  return useQuery({
    queryKey: reportKeys.financeRecords(query ? cleanQuery(query) : {}),
    queryFn: () =>
      api.get<FinanceDrilldownPage>('/reports/finance/records', {
        params: cleanQuery(query as FinanceDrilldownQuery),
      }),
    enabled: query !== null,
  });
}

/** The records behind one Inventory figure, paged by the server. */
export function useInventoryRecordsQuery(query: InventoryDrilldownQuery | null) {
  return useQuery({
    queryKey: reportKeys.inventoryRecords(query ? cleanQuery(query) : {}),
    queryFn: () =>
      api.get<{
        rows: InventoryDrilldownRow[];
        total: number;
        page: number;
        pageSize: number;
      }>('/reports/inventory/records', { params: cleanQuery(query as InventoryDrilldownQuery) }),
    enabled: query !== null,
  });
}

/**
 * The CRM tab. Communication history, WhatsApp runtime and automation rules are each present only
 * when the server authorised them; runtime and rules are as of now whatever the period.
 */
export function useCrmReportQuery(query: CrmReportQuery, { enabled = true } = {}) {
  return useQuery({
    queryKey: reportKeys.crm(cleanQuery(query)),
    queryFn: () => api.get<CrmReport>('/reports/crm', { params: cleanQuery(query) }),
    placeholderData: (previous) => previous,
    enabled,
  });
}

/** The communications or branches behind one CRM figure, paged by the server. */
export function useCrmRecordsQuery(query: CrmDrilldownQuery | null) {
  return useQuery({
    queryKey: reportKeys.crmRecords(query ? cleanQuery(query) : {}),
    queryFn: () =>
      api.get<CrmDrilldownPage>('/reports/crm/records', {
        params: cleanQuery(query as CrmDrilldownQuery),
      }),
    enabled: query !== null,
  });
}

export function useReportingSettingsQuery({ enabled = true } = {}) {
  return useQuery({
    queryKey: reportKeys.settings,
    queryFn: () => api.get<ReportingSettingsView>('/reports/settings'),
    enabled,
  });
}

/** A versioned change; a stale version comes back as a conflict and the form reloads. */
export function useUpdateReportingSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateReportingSettingsRequest) =>
      api.patch<ReportingSettingsView>('/reports/settings', body),
    onSuccess: (saved) => {
      queryClient.setQueryData(reportKeys.settings, saved);
      void queryClient.invalidateQueries({ queryKey: ['reports', 'overview'] });
    },
  });
}
